import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { planKeys } from '~/lib/query-keys';

interface DecisionBody {
  sessionId: string;
  decision: 'approve' | 'reject';
  /** FRONT-652: the id of the plan the card rendered. The backend decides THAT
   *  exact plan (rejected cleanly if it was superseded between render and
   *  click), instead of "the latest pending plan in the session". */
  planId?: number;
  reason?: string;
}

/** Pure builder for the decision payload — keeps planId/reason omission logic
 *  testable without a QueryClient. */
export function buildDecisionBody(input: {
  sessionId: string;
  decision: 'approve' | 'reject';
  planId?: number;
  reason?: string;
}): DecisionBody {
  return {
    sessionId: input.sessionId,
    decision: input.decision,
    ...(input.planId != null ? { planId: input.planId } : {}),
    ...(input.reason ? { reason: input.reason } : {}),
  };
}

/** Shape returned by POST /api/plan/decision. `background` is true when the
 *  approved plan was handed to the durable background engine — the caller uses
 *  it to suppress the inline "begin executing" cue (ADR-0027). */
export interface PlanDecisionResult {
  ok: boolean;
  background?: boolean;
}

function usePlanDecision(decision: 'approve' | 'reject') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      planId,
      reason,
    }: {
      sessionId: string;
      planId?: number;
      reason?: string;
    }) =>
      api.post<PlanDecisionResult>(
        '/api/plan/decision',
        buildDecisionBody({ sessionId, decision, planId, reason }),
      ),
    onSuccess: (_data, { sessionId }) => {
      qc.invalidateQueries({ queryKey: planKeys.bySession(sessionId) });
    },
    onError: (err) => {
      const verb = decision === 'approve' ? 'Approve' : 'Reject';
      toast.error(`${verb} failed: ${(err as Error).message}`);
    },
  });
}

export function useApprovePlan() {
  return usePlanDecision('approve');
}

export function useRejectPlan() {
  return usePlanDecision('reject');
}

/** FRONT-569: dismiss the completed "Saved to your knowledge graph" card. This
 *  persists the dismissal server-side (stamps dismissed_at) so the card stays
 *  gone when the chat is reopened — not just hidden for the current view. */
export function useDismissPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      api.post<{ ok: boolean }>('/api/plan/dismiss', { sessionId }),
    onSuccess: (_data, { sessionId }) => {
      qc.invalidateQueries({ queryKey: planKeys.bySession(sessionId) });
    },
    onError: (err) => {
      toast.error(`Couldn't dismiss the card: ${(err as Error).message}`);
    },
  });
}
